import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Ship, AlertTriangle, MapPin, Calendar, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function COSCOBoxshipArticle() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6">
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('common.back')}
        </Link>

        {/* Article Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center justify-between mb-4">
            <Badge className="bg-orange-100 text-orange-800 border-orange-200">
              {t('risk.cosco.badge')}
            </Badge>
            <div className="flex items-center text-gray-500 text-sm">
              <Calendar className="h-4 w-4 mr-1" />
              {t('risk.cosco.date')}
              <Clock className="h-4 w-4 ml-4 mr-1" />
              {t('article.coscoBoxship.readTime')}
            </div>
          </div>
          
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t('article.coscoBoxship.title')}
          </h1>
          
          <div className="relative h-64 overflow-hidden rounded-lg mb-6">
            <img 
              src="https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80"
              alt="COSCO chartered boxship port fees"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black bg-opacity-20"></div>
            <p className="absolute bottom-4 left-4 text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded">
              {t('article.coscoBoxship.imageCaption')}
            </p>
          </div>

          <p className="text-lg text-gray-700 leading-relaxed">
            {t('article.coscoBoxship.lead')}
          </p>
        </div>

        {/* Article Content */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="prose max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.coscoBoxship.section1.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.coscoBoxship.section1.content')}
            </p>

            {/* Vessel Details */}
            <Card className="mb-6 border-l-4 border-l-orange-500">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Ship className="h-5 w-5 mr-2 text-orange-600" />
                  {t('article.coscoBoxship.vessel.title')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-gray-500">{t('article.coscoBoxship.vessel.name')}:</span>
                    <div className="font-bold text-gray-900">Halley (IMO: 9275062)</div>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('article.coscoBoxship.vessel.capacity')}:</span>
                    <div className="font-bold text-gray-900">2,550 TEU</div>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('article.coscoBoxship.vessel.flag')}:</span>
                    <div className="font-bold text-gray-900">Liberia</div>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('article.coscoBoxship.vessel.charter')}:</span>
                    <div className="font-bold text-gray-900">{t('article.coscoBoxship.vessel.charterValue')}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.coscoBoxship.section2.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.coscoBoxship.section2.content')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.coscoBoxship.section3.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.coscoBoxship.section3.content')}
            </p>

            {/* Impact Assessment */}
            <div className="bg-red-50 p-6 rounded-lg mb-6 border-l-4 border-l-red-500">
              <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2" />
                {t('article.coscoBoxship.impact.title')}
              </h3>
              <ul className="text-red-700 space-y-2">
                <li>• {t('article.coscoBoxship.impact.point1')}</li>
                <li>• {t('article.coscoBoxship.impact.point2')}</li>
                <li>• {t('article.coscoBoxship.impact.point3')}</li>
                <li>• {t('article.coscoBoxship.impact.point4')}</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              {t('article.coscoBoxship.section4.title')}
            </h2>
            <p className="text-gray-700 mb-6">
              {t('article.coscoBoxship.section4.content')}
            </p>

            {/* Source Link */}
            <div className="bg-gray-50 p-6 rounded-lg mb-8 border-l-4 border-l-blue-500">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {t('article.source.title')}
              </h3>
              <p className="text-gray-600 mb-3">{t('article.source.description')}</p>
              <a 
                href="https://mykn.kuehne-nagel.com/news/article/cosco-chartered-boxship-caught"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <span className="mr-2">🚢</span>
                {t('article.source.readOriginal')}
              </a>
              <p className="text-sm text-gray-500 mt-2">
                {t('article.source.publisher')}: Kuehne+Nagel via Lloyd's List
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}